import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Typography, TextField, Button, Grid, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import CustomSelect from "../../Components/Select/CustomSelect";
import { createLead } from "../../api/services";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { SelectChangeEvent } from "@mui/material";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface FormValues {
  name: string;
  email: string;
  phoneNumber: string;
  projectName: string;
  message?: string;
}

// Updated Validation Schema using Yup
const schema = yup.object().shape({
  name: yup.string().required("Name is required").min(3, "Name must be at least 3 characters"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup
    .string()
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits") // Ensure exactly 10 digits
    .required("Phone number is required"),
  projectName: yup.string().required("Please select an option"),
  message: yup.string().min(10, "Message must be at least 10 characters"), // Made optional
});

const ContactForm = () => {
  const queryClient = useQueryClient();
  const formRef = useRef(null);
  const fieldsRef = useRef<(HTMLDivElement | null)[]>([]);
  const buttonRef = useRef(null);

  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(true); // State for disclaimer checkbox

  useEffect(() => {
    // Form Fade-in Animation
    gsap.fromTo(
      formRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: formRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Fields Staggered Animation
    gsap.fromTo(
      fieldsRef.current,
      { opacity: 0, x: -30 },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: formRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Button Pop-in Animation
    gsap.fromTo(
      buttonRef.current,
      { opacity: 0, scale: 0.8 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: buttonRef.current,
          start: "top 98%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  const { handleSubmit, control, reset, formState: { errors } } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      projectName: "",
      message: "",
    },
  });

  const addMutation = useMutation((data: FormValues) => createLead(data), {
    onSuccess: (response: any) => {
      toast.success(response.message || 'Thank you for visiting. Our sales team will contact you soon.'); // Use message from backend
      queryClient.invalidateQueries('getUsers');
      reset();
      setIsDisclaimerChecked(true); // Reset disclaimer checkbox
    },
    onError: (error: any) => {
      toast.error(`${error.response.data.message}`);
    }
  });

  const onSubmit = (data: any) => {
    if (!isDisclaimerChecked) {
      toast.error("You must agree to the disclaimer.");
      return;
    }

    const newdata = {
      ...data,
      sourceType: "Website",
      location: "Hyderabad",
      gender: "Male",
      type: "Customer",
    };
    addMutation.mutate(newdata);
  };

  return (
    <Box sx={outerBoxStyles}>
      <Box ref={formRef} sx={formContainerStyles}>
        <Typography sx={{ fontSize: "16px", mb: 3, color: 'grey', fontFamily: "LEMON MILK" }}>
          Book a site visit Now!
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Disclaimer Checkbox */}
          
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            {/* Name Field */}
            <Grid item xs={12} sm={6} ref={(el) => (fieldsRef.current[0] = el)}>
              <Typography sx={labelStyles}>Name</Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth placeholder="Full Name " variant="outlined" sx={inputStyles} error={!!errors.name} helperText={errors.name?.message} />
                )}
              />
            </Grid>

            {/* Email Field */}
            <Grid item xs={12} sm={6} ref={(el) => (fieldsRef.current[1] = el)}>
              <Typography sx={labelStyles}>Email</Typography>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth placeholder="@gmail.com" variant="outlined" sx={inputStyles} error={!!errors.email} helperText={errors.email?.message} />
                )}
              />
            </Grid>

            {/* phoneNumber Field */}
            <Grid item xs={12} sm={6} ref={(el) => (fieldsRef.current[2] = el)}>
              <Typography sx={labelStyles}>Phone Number</Typography>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="98XXXXXXXX"
                    variant="outlined"
                    sx={inputStyles}
                    inputProps={{ maxLength: 10 }} // Restrict input to 10 digits
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
            </Grid>

            {/* Select Option Field (MUI Select) */}
            <Grid item xs={12} sm={6} ref={(el) => (fieldsRef.current[3] = el)}>
              <Typography sx={labelStyles}>Select Project</Typography>
              <Controller
                name="projectName"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.projectName} sx={selectStyles}>
                    <CustomSelect
                      options={["Luxor Exclusive", "Luxuria"]}
                      value={field.value || ""} // ✅ Fix: Ensures a string is passed
                      onChange={(value) => field.onChange(value)} // ✅ Fix: Works for both single and multiple select
                      multiple={false} // ✅ Ensures single selection mode
                      placeholder="Select a Project"
                    />
                  </FormControl>
                )}
              />
              <Typography sx={{ color: "red", fontSize: "12px", mt: 1 }}>{errors.projectName?.message}</Typography>
            </Grid>

            {/* Message Field (Optional) */}
            <Grid item xs={12} ref={(el) => (fieldsRef.current[4] = el)}>
              <Typography sx={labelStyles}>Message</Typography>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Your Message here..."
                    variant="outlined"
                    multiline
                    rows={4}
                    sx={{
                      ...inputStyles,
                      "& .MuiOutlinedInput-root": {
                        ...inputStyles["& .MuiOutlinedInput-root"],
                        "& textarea::placeholder": { color: "#B3B3B3" },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                      },
                    }}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item xs={12}>
                <FormControl>
                <label style={{ display: "flex", alignItems: "flex-start", color: "#B3B3B3", fontSize: "10px", textAlign: "left", lineHeight: "1.4" }}>
                  <input
                  type="checkbox"
                  checked={isDisclaimerChecked}
                  onChange={(e) => setIsDisclaimerChecked(e.target.checked)}
                  style={{
                    marginRight: "8px",
                    width: "28px", // Larger checkbox size
                    height: "28px", // Larger checkbox size
                    accentColor: "#bf742c", // Primary color when checked
                    cursor: "pointer",
                  }}
                  />
                  <span style={{ color: "#A3A3A3", fontSize: "11px", textAlign: "left", lineHeight: "1.5" }}>
                  I authorize Kolla Group and its representatives to Call, SMS, Email or WhatsApp me about its updates and notifications. This consent overrides any registration for DND / NDNC.
                  </span>
                </label>
                </FormControl>
            </Grid>
          </Grid>


          {/* Submit Button */}
          <Button ref={buttonRef} type="submit" variant="contained" sx={submitButtonStyles}>
            Submit
          </Button>
        </form>
      </Box>
    </Box>
  );
};

// Styles
const outerBoxStyles = {
  width: "100%",
  backgroundColor: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: { xs: 1, md: 4 },
  py: { xs: 2, md: 4 },
};

const formContainerStyles = {
  width: "100%",
  maxWidth: "700px",
  textAlign: "center",
  background: "linear-gradient(135.54deg,rgb(7, 7, 7) 0%,rgb(7, 7, 7) 92.05%)",
  paddingX: { xs: "24px", md: "50px" },
  paddingY: { xs: "24px", md: "30px" },
  borderRadius: "8px",
  border: "2px solid transparent",
  backgroundClip: "padding-box, border-box",
  borderImageSource: "linear-gradient(120.84deg, #B3B3B3 -12.48%, #BA6F2A 101.74%)",
  borderImageSlice: 1,
  boxShadow: "6px 6px 10px 0px #23232340",
};

const titleStyles = {
  fontWeight: "600",
  fontSize: "28px",
  background: "linear-gradient(90deg, #bf742c, rgb(252, 243, 202), #c58038)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  display: "inline-block",
  fontFamily: "LEMON MILK",
  mb: 1,
};

const inputStyles = {
  backgroundColor: "#000000", // Dark background
  borderRadius: "6px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#444" },
    "&:hover fieldset": { borderColor: "#BA6F2A" },
    "&.Mui-focused fieldset": { borderColor: "#F5D597" },
    // placeholder as grey 
    "& input::placeholder": { color: "#B3B3B3" },
  },
  input: { color: "white", padding: "10px 12px" },
};

const selectStyles = {
  backgroundColor: "#222",
  borderRadius: "6px",
  "& .MuiOutlinedInput-root": { height: "40px" },
  "& .MuiSelect-select": { height: "40px", display: "flex", alignItems: "center", color: "grey", padding: "10px 12px" },
};

const labelStyles = { color: "white", textAlign: "left", fontSize: "12px", mb: 1 , fontFamily: "LEMON MILK",};

const submitButtonStyles = {
  mt: 3,
  background: "linear-gradient(90deg, #bf742c,rgb(240, 171, 97), #c58038)",
  color: "#000000",
  padding: "10px 24px",
  fontSize: "16px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: "600",
  transition: "background 0.3s, transform 0.3s",
  boxShadow : "0px 4px 10px rgba(134, 88, 23, 0.18)",
  '&:hover': {
    background: "linear-gradient(90deg, #c58038,rgb(240, 171, 97), #bf742c)",
    transform : "scale(1.05)",
    boxShadow: "0px 4px 15px rgba(159, 128, 84, 0.5)",
  },
  '&:focus': {
    background: "linear-gradient(90deg, #c58038,rgb(240, 171, 97), #bf742c)",
  },

};

export default ContactForm;
